import {
	CONSTELLATION_CENTER,
	CONSTELLATION_DOT_COUNT,
	CONSTELLATION_FORMATION_RADIUS,
	CONSTELLATION_SIZE,
	GOLDEN_RATIO,
} from '../constants';
import type { ConstellationDot } from '../types';
import { getGlowSparkStrength, getStableVariation } from './poseHelpers';

type EqualizerDotInput = Readonly<{
	color: string;
	index: number;
	timeSeconds: number;
}>;

const MIN_FREQUENCY_RADIANS_PER_SECOND = 2.6;
const FREQUENCY_RANGE_RADIANS_PER_SECOND = 2.5;
const FREQUENCY_VARIATION_OFFSET = 8;
const DOT_PHASE_OFFSET_RADIANS = 0.72;
const PRIMARY_WAVE_WEIGHT = 0.618;
const SECONDARY_WAVE_WEIGHT = 0.382;
const SECONDARY_PHASE_RATIO = 0.7;
const MIN_AMPLITUDE_RATIO = 0.09;
const AMPLITUDE_RANGE_RATIO = 0.11;
const AMPLITUDE_VARIATION_OFFSET = 11;

const GLOW_CONCENTRATION_POWER = 2;
const MAXIMUM_GLOW_STRENGTH = 0.55;

export const createEqualizerDot = ({
	color,
	index,
	timeSeconds,
}: EqualizerDotInput): ConstellationDot => {
	const frequency =
		MIN_FREQUENCY_RADIANS_PER_SECOND +
		FREQUENCY_RANGE_RADIANS_PER_SECOND * getStableVariation(index + FREQUENCY_VARIATION_OFFSET);
	const phase = index * DOT_PHASE_OFFSET_RADIANS;

	// Blends two phase-shifted sine waves into one irregular motion.
	const wave =
		PRIMARY_WAVE_WEIGHT * Math.sin(timeSeconds * frequency + phase) +
		SECONDARY_WAVE_WEIGHT *
			// The golden ratio keeps the secondary wave from repeating the primary wave's cycle.
			Math.sin(timeSeconds * frequency * GOLDEN_RATIO - phase * SECONDARY_PHASE_RATIO);
	const amplitude =
		CONSTELLATION_SIZE *
		(MIN_AMPLITUDE_RATIO +
			AMPLITUDE_RANGE_RATIO * getStableVariation(index + AMPLITUDE_VARIATION_OFFSET));

	return {
		brightness: 0.45 + 0.55 * Math.min(1, Math.abs(wave)),
		color,
		glowStrength: getGlowSparkStrength(
			Math.abs(wave),
			GLOW_CONCENTRATION_POWER,
			MAXIMUM_GLOW_STRENGTH,
		),
		scale: 0.62,
		x:
			CONSTELLATION_CENTER -
			CONSTELLATION_FORMATION_RADIUS +
			(index / (CONSTELLATION_DOT_COUNT - 1)) * CONSTELLATION_FORMATION_RADIUS * 2,
		y: CONSTELLATION_CENTER + wave * amplitude,
	};
};
